import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import sideNavTranslations from 'i2l?query=sporks!../../lang/sideNav/en.lyaml';
import { useGetPublicProductGroupsLazyQuery } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.hooks';
import SharedSideNav from 'pricing-pages-ui-components/components/layout/side-nav/SideNav';
import { useScopedTranslations } from 'pricing-pages-ui-components/i18n/useScopedTranslations';
import { prefetchPublicPage } from '../setup/prefetchPage';
import { useLocation } from 'quartz-routing/react-router';
import { useCallback, useContext } from 'react';
import { PublicI18nContext } from '../context/I18nContext';
import { useCurrency } from '../context/CurrencyContext';
import { getCountryCodeFromLocale } from '../utils/countryCodeUtils';
import { isCountryCodeString } from '../utils/currencyTypes';
import { getBrowserLocale } from '../utils/getPreferredLanguage';
import { getProductGroupBySlug } from '../utils/getProductGroupBySlug';
import PublicCurrencyPicker from './PublicCurrencyPicker';
import { jsx as _jsx } from "react/jsx-runtime";
const PublicSideNav = ({
  selectedNavItem
}) => {
  var _sections$flatMap$fin, _sections$flatMap$fin2;
  const {
    search
  } = useLocation();
  const {
    i18nLoader
  } = useContext(PublicI18nContext);
  const {
    isReady,
    Skeleton
  } = useScopedTranslations(sideNavTranslations, i18nLoader);
  const {
    displayedCurrencyCode
  } = useCurrency();
  const [fetchProductGroup] = useGetPublicProductGroupsLazyQuery();
  const prefetchProductGroupData = useCallback(navItem => {
    const productGroup = getProductGroupBySlug(navItem);
    if (!productGroup) return;
    const rawCountry = getCountryCodeFromLocale();
    void fetchProductGroup({
      variables: {
        productGroup,
        currencyCode: displayedCurrencyCode,
        preferredCountry: isCountryCodeString(rawCountry) ? rawCountry : undefined,
        browserLocale: getBrowserLocale()
      }
    });
  }, [displayedCurrencyCode, fetchProductGroup]);

  // Guard unescapedText calls behind isReady — these are imperative and fire
  // before translations register on first render, producing "Missing translation"
  // errors. Skeleton hides the output anyway while loading.
  const sections = isReady ? [{
    section: 'bundle',
    headingText: unescapedText('sideNav.sections.bundle'),
    items: [{
      navItem: 'suite',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.suite"
      }),
      to: {
        pathname: '/suite',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('suite');
        prefetchProductGroupData('suite');
      }
    }, {
      navItem: 'crm',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.crm"
      }),
      to: {
        pathname: '/crm',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('crm');
        prefetchProductGroupData('crm');
      }
    }, {
      navItem: 'bundle',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.bundle"
      }),
      to: {
        pathname: '/bundle',
        search
      },
      onMouseEnter: () => prefetchPublicPage('bundle')
    }, {
      navItem: 'marketing-plus',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.marketing-plus"
      }),
      to: {
        pathname: '/marketing-plus',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('marketing-plus');
        prefetchProductGroupData('marketing-plus');
      }
    }]
  }, {
    section: 'core',
    headingText: unescapedText('sideNav.sections.core'),
    items: [{
      navItem: 'smart-crm',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.smart-crm"
      }),
      to: {
        pathname: '/smart-crm',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('smart-crm');
        prefetchProductGroupData('smart-crm');
      }
    }, {
      navItem: 'marketing',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.marketing"
      }),
      to: {
        pathname: '/marketing',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('marketing');
        prefetchProductGroupData('marketing');
      }
    }, {
      navItem: 'sales',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.sales"
      }),
      to: {
        pathname: '/sales',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('sales');
        prefetchProductGroupData('sales');
      }
    }, {
      navItem: 'service',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.service"
      }),
      to: {
        pathname: '/service',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('service');
        prefetchProductGroupData('service');
      }
    }, {
      navItem: 'content',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.content"
      }),
      to: {
        pathname: '/content',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('content');
        prefetchProductGroupData('content');
      }
    }, {
      navItem: 'data',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.data"
      }),
      to: {
        pathname: '/data',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('data');
        prefetchProductGroupData('data');
      }
    }, {
      navItem: 'commerce',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.commerce"
      }),
      to: {
        pathname: '/commerce',
        search
      },
      onMouseEnter: () => {
        prefetchPublicPage('commerce');
        prefetchProductGroupData('commerce');
      }
    }]
  }] : [];

  // Derive selectedItemLabel from sections to avoid duplicating keys.
  const selectedItemLabel = (_sections$flatMap$fin = (_sections$flatMap$fin2 = sections.flatMap(s => s.items).find(item => item.navItem === selectedNavItem)) === null || _sections$flatMap$fin2 === void 0 ? void 0 : _sections$flatMap$fin2.title) !== null && _sections$flatMap$fin !== void 0 ? _sections$flatMap$fin : selectedNavItem;
  return (
    /*#__PURE__*/
    // 11 nav items × ~1 line each; widths approximate label lengths
    _jsx(Skeleton, {
      widths: ['60%', '40%', '55%', '65%', '50%', '45%', '55%', '45%', '50%', '40%', '55%'],
      heights: [56, 56, 56, 56, 56, 56, 56, 56, 56, 56, 56],
      children: /*#__PURE__*/_jsx(SharedSideNav, {
        selectedNavItem: selectedNavItem,
        selectedItemLabel: selectedItemLabel,
        menuAriaLabel: isReady ? unescapedText('sideNav.toggleMenu') : '',
        sections: sections,
        footerSlot: /*#__PURE__*/_jsx(PublicCurrencyPicker, {}),
        desktopPosition: "sticky",
        trackGlobalNavHeight: true
      })
    })
  );
};
export default PublicSideNav;